import { Box, Collapse, Drawer as MuiDrawer } from "@mui/material";
import { CSSObject, styled, Theme } from "@mui/material/styles";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SidebarMenu, USER_TYPE2 } from "src/constants/common";
import { useAppSelector, useAppDispatch } from "src/redux/store";
import { getAllProjectsData } from "src/redux/actions";
import { setOpen } from "src/redux/slices/newProjectSetup";

import { CustomStyles, getStyles } from "src/styles/theme";
import LogoWrapper from "./LogoWrapper";
import defaultStyles, { StylesClasses } from "./styles";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

type SideBarProps = {
  customStyles?: CustomStyles<StylesClasses>;
};

const drawerWidth = 270;

const paperScroll = (theme: Theme): CSSObject => ({
  position: "fixed",
  top: 0,
  height: "100vh",
  overflowY: "auto",
  overflowX: "hidden",
  backgroundColor: theme.palette.custom.background.dark,
  borderRight: "none",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(255, 255, 255, 0.25) transparent",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255, 255, 255, 0.25)",
    borderRadius: "3px",
  },
});

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": { ...openedMixin(theme), ...paperScroll(theme) },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": { ...closedMixin(theme), ...paperScroll(theme) },
  }),
}));

const SideBar = ({ customStyles }: SideBarProps) => {
  const [open, setOpen] = useState(true);
  const styles = getStyles<StylesClasses>(defaultStyles, customStyles);
  const [isNewSelected, setNewSelected] = useState(
    window.localStorage.getItem("new_selection")
  );
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  const user = useAppSelector((state) => state.root.user);
  const [openOption, setOpenOption] = useState(-1);

  const dispatch = useAppDispatch(); //for eco-restoration button

  useEffect(() => {
    setNewSelected(window.localStorage.getItem("new_selection"));
  }, []);

  const filteredMenu = useMemo(() => {
    return SidebarMenu.filter((item) => {
      const userTypeMatch = item.type.includes(user.data.userType);

      // If menu item specifies showForNewEntity, apply condition
      if (
        user.data.userType === USER_TYPE2.ENTITY &&
        "showForNewEntity" in item &&
        isNewSelected === "0"
      ) {
        return userTypeMatch && item.showForNewEntity === false;
      }

      if (
        user.data.userType === USER_TYPE2.ENTITY &&
        "showForNewEntity" in item &&
        isNewSelected === "1"
      ) {
        return userTypeMatch && item.showForNewEntity === true;
      }

      return userTypeMatch;
    });
  }, [user.data.userType, isNewSelected]);

  const menuPaths = filteredMenu.reduce<string[]>((acc, item) => {
    if (item.path) acc.push(item.path);
    item.options?.forEach((option) => option.path && acc.push(option.path));
    return acc;
  }, []);
  const isOnMenuPath = menuPaths.includes(location.pathname);
  const [activeMenuPath, setActiveMenuPath] = useState<string>(() =>
    isOnMenuPath
      ? location.pathname
      : localStorage.getItem("activeMenuPath") || ""
  );

  useEffect(() => {
    if (isOnMenuPath) {
      setActiveMenuPath(location.pathname);
      localStorage.setItem("activeMenuPath", location.pathname);
    }
  }, [location.pathname, isOnMenuPath]);

  const highlightedPath = isOnMenuPath ? location.pathname : activeMenuPath;
  const isActive = (itemPath?: string) =>
    !!itemPath && itemPath === highlightedPath;

  useEffect(() => {
    const idx = filteredMenu.findIndex((item) =>
      item.options?.some((option) => option.path === highlightedPath)
    );
    if (idx !== -1) setOpenOption(idx);
  }, [highlightedPath, filteredMenu]);

  return (
    <Drawer variant="permanent" open={open}>
      <Box {...styles("container")}>
        <Box>
          <LogoWrapper onClick={handleDrawerOpen} open={open} />
        </Box>
        <Box>
          <Box {...styles("divider")}></Box>

          {filteredMenu.map((item, index) => {
            const sectionLabel =
              "sectionLabel" in item ? item.sectionLabel : undefined;

            return (
              <Fragment key={item.id ?? item.text ?? index}>
                {sectionLabel &&
                  (open ? (
                    <Box {...styles("sectionLabel")}>{sectionLabel}</Box>
                  ) : (
                    <Box {...styles("sectionLabelCollapsed")} />
                  ))}
                <Box sx={{}}>
                  <Box
                    {...styles("navItemsContainer", {
                      ...(isActive(item.path) &&
                        defaultStyles.activeNavContainer),
                      paddingLeft: open ? "16px" : "8px",
                    })}
                    onClick={() => {
                      if (item.options) return;
                      item.path ? navigate(item.path) : console.log("++++");
                    }}
                  >
                    <Box component="img" src={item.icon} />
                    <Box
                      {...styles("navText", {
                        ...(isActive(item.path) && defaultStyles.activeNavText),
                        display: open ? "flex" : "none",
                        gap: "5px",
                        alignItems: "center",
                      })}
                      onClick={() => {
                        setOpenOption(openOption === index ? -1 : index);
                      }}
                    >
                      {item.text}
                      {item.options && (
                        <KeyboardArrowDownRoundedIcon
                          sx={{ color: "white" }}
                          // onClick={() => {
                          //   setOpenOption(openOption === index ? -1 : index);
                          // }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Collapse
                    in={item.options && openOption === index}
                    sx={{
                      // backgroundColor: "#87A96B",
                      width: "100%",
                      paddingLeft: "10px",
                    }}
                  >
                    {item.options?.map((option) => (
                      <Box
                        {...styles("navItemsContainer", {
                          ...(isActive(option.path) &&
                            defaultStyles.activeNavContainer),
                          paddingLeft: open ? "16px" : "8px",
                        })}
                        onClick={() => {
                          option.path
                            ? navigate(option.path)
                            : console.log("++++");
                        }}
                      >
                        <Box component="img" src={option.icon} />
                        <Box
                          {...styles("navText", {
                            ...(isActive(option.path) &&
                              defaultStyles.activeNavText),
                            display: open ? "flex" : "none",
                            gap: "5px",
                            alignItem: "center",
                          })}
                        >
                          {option.text}
                        </Box>
                      </Box>
                    ))}
                  </Collapse>
                </Box>
              </Fragment>
            );
          })}
        </Box>
      </Box>
    </Drawer>
  );
};

export default SideBar;
